import {
  ProductCardConfiguration,
  productCardConfigurationDefaults,
  ProductCardConfigurationKeys,
  PRODUCT_CARD_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/product-card-rules';
import {
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys,
  PBasketConfigurations,
  pBasketConfigurationsDefaultValues,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import { ensureArray } from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';

export const selectHideMarkupRule = selectConfigurationValueFrom<
  ProductCardConfigurationKeys.HideMarkupPrice,
  ProductCardConfiguration
>(
  PRODUCT_CARD_FEATURE_NAME,
  ProductCardConfigurationKeys.HideMarkupPrice,
  productCardConfigurationDefaults[
    ProductCardConfigurationKeys.HideMarkupPrice
  ],
);

export const selectHiddenFilterIds = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.HiddenFilters,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.HiddenFilters,
    pBasketConfigurationsDefaultValues[PBasketConfigurationKeys.HiddenFilters],
  ),
  ensureArray,
);

export const selectHiddenFilterTitleIds = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.HiddenFilterTitles,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.HiddenFilterTitles,
    pBasketConfigurationsDefaultValues[
      PBasketConfigurationKeys.HiddenFilterTitles
    ],
  ),
  ensureArray,
);

export const selectHiddenFilterValues = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.HiddenFilterValues,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.HiddenFilterValues,
    pBasketConfigurationsDefaultValues[
      PBasketConfigurationKeys.HiddenFilterValues
    ],
  ),
  (hiddenValues: string) => {
    try {
      return JSON.parse(hiddenValues) as Record<string, string[]>;
    } catch (e) {
      console.error('Failed to parse hidden filter values config: ', e);
      return {};
    }
  },
);

export const selectFiltersOrderRule = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.FiltersOrder,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.FiltersOrder,
    pBasketConfigurationsDefaultValues[PBasketConfigurationKeys.FiltersOrder],
  ),
  ensureArray,
);

export const selectFiltersOpenedIdsRule = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.FiltersOpenedIds,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.FiltersOpenedIds,
    pBasketConfigurationsDefaultValues[
      PBasketConfigurationKeys.FiltersOpenedIds
    ],
  ),
  ensureArray,
);

export const selectShowWspdPriceRule = selectConfigurationValueFrom<
  ProductCardConfigurationKeys.ShowWspdPrice,
  ProductCardConfiguration
>(
  PRODUCT_CARD_FEATURE_NAME,
  ProductCardConfigurationKeys.ShowWspdPrice,
  productCardConfigurationDefaults[ProductCardConfigurationKeys.ShowWspdPrice],
);

export const selectProductIdsFieldsRule = selectConfigurationValueFrom<
  ProductCardConfigurationKeys.ProductIdFormat,
  ProductCardConfiguration
>(
  PRODUCT_CARD_FEATURE_NAME,
  ProductCardConfigurationKeys.ProductIdFormat,
  productCardConfigurationDefaults[
    ProductCardConfigurationKeys.ProductIdFormat
  ],
);

export const selectExpandButtonPeekRule = selectConfigurationValueFrom<
  ProductCardConfigurationKeys.ExpandButtonPeek,
  ProductCardConfiguration
>(
  PRODUCT_CARD_FEATURE_NAME,
  ProductCardConfigurationKeys.ExpandButtonPeek,
  productCardConfigurationDefaults[
    ProductCardConfigurationKeys.ExpandButtonPeek
  ],
);

export const selectProductTitleFieldsRule = selectConfigurationValueFrom<
  ProductCardConfigurationKeys.ProductTitleFormat,
  ProductCardConfiguration
>(
  PRODUCT_CARD_FEATURE_NAME,
  ProductCardConfigurationKeys.ProductTitleFormat,
  productCardConfigurationDefaults[
    ProductCardConfigurationKeys.ProductTitleFormat
  ],
);

export const selectResetDefaultPresetOnAccountChangeEnabled =
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.ResetDefaultPresetOnAccountChange,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.ResetDefaultPresetOnAccountChange,
    pBasketConfigurationsDefaultValues[
      PBasketConfigurationKeys.ResetDefaultPresetOnAccountChange
    ],
  );

export const selectResetPresetOnLogoClickEnabled = selectConfigurationValueFrom<
  PBasketConfigurationKeys.ResetPresetOnLogoClick,
  PBasketConfigurations
>(
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys.ResetPresetOnLogoClick,
  pBasketConfigurationsDefaultValues[
    PBasketConfigurationKeys.ResetPresetOnLogoClick
  ],
);

export const selectFilterShortcutConfig = selectConfigurationValueFrom<
  PBasketConfigurationKeys.FilterShortcutConfig,
  PBasketConfigurations
>(
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys.FilterShortcutConfig,
  undefined,
);

export const selectCatalogOnlyDefaultRule = selectConfigurationValueFrom<
  PBasketConfigurationKeys.CatalogOnlyDefault,
  PBasketConfigurations
>(
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys.CatalogOnlyDefault,
  pBasketConfigurationsDefaultValues[
    PBasketConfigurationKeys.CatalogOnlyDefault
  ],
);

export const selectBoughtThisSeasonButtonPropertiesRule = createSelector(
  selectConfigurationValueFrom<
    PBasketConfigurationKeys.BoughtThisSeasonButtonProperties,
    PBasketConfigurations
  >(
    NAMESPACE_PRODUCT_BASKET,
    PBasketConfigurationKeys.BoughtThisSeasonButtonProperties,
    pBasketConfigurationsDefaultValues[
      PBasketConfigurationKeys.BoughtThisSeasonButtonProperties
    ],
  ),
  (boughtThisSeasonButtonProperties: string) => {
    try {
      return JSON.parse(boughtThisSeasonButtonProperties);
    } catch (e) {
      console.warn(
        'Failed to parse bought this season button properties config: ',
        e,
      );
      return null;
    }
  },
);
